import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { payload, payloadOrNullOn404 } from "./envelope";
import type { components } from "./schema";

export type StripeAccount = components["schemas"]["StripeAccount"];
export type DepositPolicy = components["schemas"]["DepositPolicy"];
export type DepositPolicyRequest = components["schemas"]["DepositPolicyRequest"];
export type Deposit = components["schemas"]["Deposit"];

/**
 * Status da conta Stripe conectada, lido de um cache LOCAL (atualizado pelo
 * webhook `account.updated` + reconciliação diária) — nunca bate na Stripe.
 *
 * `null` = conta nunca conectada. É o estado inicial de todo
 * estabelecimento, não um erro: conectar é opcional, o painel funciona 100%
 * sem Stripe — só a feature de sinal depende disso.
 *
 * A API responde 200 com `data: null` nesse caso. O tratamento de 404
 * continua aqui de propósito: durante um deploy, esta versão do painel
 * conversa por alguns minutos com a API anterior, que ainda responde 404 —
 * e sem ele a tela de pagamento quebraria justamente nessa janela.
 */
export function stripeStatusQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "stripe"] as const,
    queryFn: () =>
      payloadOrNullOn404(
        api.GET("/establishments/{id}/stripe/status", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * Inicia o onboarding Stripe Connect Recipient. O dono completa KYC e dados
 * bancários na página hospedada da Stripe; o front recebe somente a URL.
 */
export function connectStripe(
  establishmentId: string,
  returnUrl?: string,
): Promise<{ onboarding_url: string }> {
  return payload(
    api.POST("/establishments/{id}/stripe/connect", {
      params: { path: { id: establishmentId } },
      body: returnUrl ? { return_url: returnUrl } : {},
    }),
  );
}

/** Cria um novo link de onboarding para uma conta pendente. */
export function resumeStripeConnect(
  establishmentId: string,
  returnUrl?: string,
): Promise<{ onboarding_url: string }> {
  return payload(
    api.POST("/establishments/{id}/stripe/onboarding-link", {
      params: { path: { id: establishmentId } },
      body: returnUrl ? { return_url: returnUrl } : {},
    }),
  );
}

/**
 * Deposit-policy GERAL do estabelecimento. `null` (404) = nunca configurada
 * — estado normal enquanto o dono não mexe em sinal.
 */
export function depositPolicyQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "deposit-policy"] as const,
    queryFn: () =>
      payloadOrNullOn404(
        api.GET("/establishments/{id}/deposit-policy", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * `type: percentage` aceita 1-100; `type: fixed` aceita qualquer valor > 0
 * em centavos (o teto "não passar do preço do serviço" só é checável no
 * nível serviço). Configurar NÃO exige Stripe conectada — só ativar
 * `deposit_required` num serviço exige.
 */
export function upsertDepositPolicy(
  establishmentId: string,
  input: DepositPolicyRequest,
): Promise<DepositPolicy> {
  return payload(
    api.PUT("/establishments/{id}/deposit-policy", {
      params: { path: { id: establishmentId } },
      body: input,
    }),
  );
}

/**
 * Deposit-policy EFETIVA de um serviço: a específica do serviço se existir,
 * senão a geral do estabelecimento. `null` (404) = nenhuma das duas existe.
 */
export function serviceDepositPolicyQuery(serviceId: string) {
  return queryOptions({
    queryKey: ["service", serviceId, "deposit-policy"] as const,
    queryFn: () =>
      payloadOrNullOn404(
        api.GET("/services/{id}/deposit-policy", {
          params: { path: { id: serviceId } },
        }),
      ),
  });
}

/**
 * Sobrescreve a geral do estabelecimento pra esse serviço. Único nível onde
 * `type: fixed` é validado contra o preço (`422
 * deposit_value_exceeds_service_price`).
 */
export function upsertServiceDepositPolicy(
  serviceId: string,
  input: DepositPolicyRequest,
): Promise<DepositPolicy> {
  return payload(
    api.PUT("/services/{id}/deposit-policy", {
      params: { path: { id: serviceId } },
      body: input,
    }),
  );
}

/**
 * Depósito de um agendamento. `null` (404) = serviço sem sinal ou worker
 * `create_deposit` ainda não processou — normal pra qualquer agendamento que
 * nasceu `confirmed` direto.
 */
export function appointmentDepositQuery(appointmentId: string) {
  return queryOptions({
    queryKey: ["appointment", appointmentId, "deposit"] as const,
    queryFn: () =>
      payloadOrNullOn404(
        api.GET("/appointments/{id}/deposit", {
          params: { path: { id: appointmentId } },
        }),
      ),
  });
}
